import { useEffect } from "react"
import Sticky from "react-stickynode"
import { useTranslation } from "next-i18next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import dynamic from "next/dynamic"
import MetaData from "@/common/components/MetaData"
import SectionLine from "@/common/components/_UI/SectionLine"

import Swiper1 from "@/common/assets/image/solutions/gamefi/Swiper1.webp"
import Swiper2 from "@/common/assets/image/solutions/gamefi/Swiper2.webp"
import Swiper3 from "@/common/assets/image/solutions/gamefi/Swiper3.webp"
import SubDesc1 from "@/common/assets/image/solutions/gamefi/SubDesc1.svg"
import SubDesc2 from "@/common/assets/image/solutions/gamefi/SubDesc2.svg"
import SubDesc3 from "@/common/assets/image/solutions/gamefi/SubDesc3.svg"
import SubDesc4 from "@/common/assets/image/solutions/gamefi/SubDesc4.svg"
import YGGJProfile from "@/common/assets/image/homepage/YGGJZed.png"
import YGGJBg from "@/common/assets/image/solutions/gamefi/CaseStickey.webp"

const NavbarNew = dynamic(import("@/containers/common/NavbarNew"))

import { DarkBGWrapper, LayoutWrapper } from "@/common/components/LayoutStyle"
import Description from "@/common/components/Description"
import { ProductBanner } from "@/common/components/ProductBanner"
import {
  ProductNames,
  SolutionNames,
  SolutionPlans
} from "@/common/data/Products"
import Footer from "@/containers/common/FooterNew"
import NextImg from "@/common/components/NextImg"
import { PricingBoard } from "@/common/components/Pricing"
import { useRouter } from "next/router"
import { SuccessStory } from "@/common/components/SuccessStory"
import ContactUsSection from "@/common/components/ContactUs/ContactUsSection"

const GameFiPage = () => {
  const { t } = useTranslation(["gamefi"])
  const router = useRouter()

  useEffect(() => {
    const { plan } = router.query
    if (plan != null) {
      setTimeout(() => {
        document.getElementById("contactUsSection")?.scrollIntoView({
          behavior: "smooth",
          block: "start"
        })
      }, 200)
    }
  })

  return (
    <>
      <MetaData
        title={t("meta.title")}
        description={t("meta.description")}
        url={t("meta.url")}
        image="https://storage.googleapis.com/kryptogo-official-website-public/og-logo.webp"
        languageAlternates={[
          {
            hrefLang: "en-US",
            href: "https://www.kryptogo.com/solutions/gamefi"
          },
          {
            hrefLang: "zh-TW",
            href: "https://www.kryptogo.com/tw/solutions/gamefi"
          },
          {
            hrefLang: "zh-CN",
            href: "https://www.kryptogo.com/cn/solutions/gamefi"
          },
          {
            hrefLang: "ja-JP",
            href: "https://www.kryptogo.com/ja/solutions/gamefi"
          }
        ]}
      />

      <Sticky top={0} innerZ={1000} activeClass="sticky-active">
        <NavbarNew
          contactOnClick={e => {
            e.preventDefault()
            document.getElementById("contactUsSection")?.scrollIntoView({
              behavior: "smooth",
              block: "start"
            })
          }}
        />
      </Sticky>
      <DarkBGWrapper></DarkBGWrapper>
      <LayoutWrapper>
        <ProductBanner
          className="kg-text-shadow"
          bannerType={SolutionNames.GameFi}
          title={t("Banner.productName")}
          titleh1={t("Banner.title")}
          description={t("Banner.description")}
          primaryButton={{
            content: t("Banner.button1"),
            onClick: (e: any) => {
              e.preventDefault()
              document.getElementById("GameFiDesc1")?.scrollIntoView({
                behavior: "smooth",
                block: "start"
              })
            }
          }}
          subtleButton={{
            content: t("Banner.button2"),
            onClick: (e: any) => {
              e.preventDefault()
              document.getElementById("contactUsSection")?.scrollIntoView({
                behavior: "smooth",
                block: "start"
              })
            }
          }}
        />
        <SectionLine style={{ marginBottom: "-60px" }} />
        <Description
          id="GameFiDesc1"
          backgroundType="envelope"
          items={[
            {
              titles: [{ text: t("Desc.1.title"), textType: "h3" }],
              content: t("Desc.1.content"),
              checkList: [
                {
                  title: t("Desc.1.checkList.1.title"),
                  list: [
                    { description: t("Desc.1.checkList.1.item.1") },
                    { description: t("Desc.1.checkList.1.item.2") },
                    { description: t("Desc.1.checkList.1.item.3") },
                    { description: t("Desc.1.checkList.1.item.4") }
                  ]
                },
                {
                  title: t("Desc.1.checkList.2.title"),
                  list: [
                    { description: t("Desc.1.checkList.2.item.1") },
                    { description: t("Desc.1.checkList.2.item.2") },
                    { description: t("Desc.1.checkList.2.item.3") },
                    { description: t("Desc.1.checkList.2.item.4") }
                  ]
                },
                {
                  title: t("Desc.1.checkList.3.title"),
                  list: [
                    { description: t("Desc.1.checkList.3.item.1") },
                    { description: t("Desc.1.checkList.3.item.2") },
                    { description: t("Desc.1.checkList.3.item.3") }
                  ]
                }
              ],
              wide: true
            }
          ]}
        />
        <Description
          backgroundType="none"
          items={[
            {
              titles: [{ text: t("Desc.2.title"), textType: "h3" }],
              content: t("Desc.2.description"),
              subDescriptions: [
                {
                  icon: SubDesc1,
                  title: t("Desc.2.subDesc.0.title"),
                  content: t("Desc.2.subDesc.0.description")
                },
                {
                  icon: SubDesc2,
                  title: t("Desc.2.subDesc.1.title"),
                  content: t("Desc.2.subDesc.1.description")
                },
                {
                  icon: SubDesc3,
                  title: t("Desc.2.subDesc.2.title"),
                  content: t("Desc.2.subDesc.2.description")
                },
                {
                  icon: SubDesc4,
                  title: t("Desc.2.subDesc.3.title"),
                  content: t("Desc.2.subDesc.3.description")
                }
              ],
              wide: true
            }
          ]}
        />
        <ProductBanner
          titleh3={t("swiper.items.1.title")}
          titleh3Type="normal"
          description={t("swiper.items.1.description")}
          comp2={<NextImg src={Swiper2} />}
          checkList={[
            {
              list: [
                {
                  title: t("swiper.items.1.checkList.0.title"),
                  description: t("swiper.items.1.checkList.0.description")
                },
                {
                  title: t("swiper.items.1.checkList.1.title"),
                  description: t("swiper.items.1.checkList.1.description")
                },
                {
                  title: t("swiper.items.1.checkList.2.title"),
                  description: t("swiper.items.1.checkList.2.description")
                },
                {
                  title: t("swiper.items.1.checkList.3.title"),
                  description: t("swiper.items.1.checkList.3.description")
                }
              ]
            }
          ]}
        />
        <ProductBanner
          comp2Position="left"
          titleh3={t("swiper.items.0.title")}
          titleh3Type="normal"
          description={t("swiper.items.0.description")}
          comp2={<NextImg src={Swiper1} />}
          checkList={[
            {
              list: [
                {
                  title: t("swiper.items.0.checkList.0.title"),
                  description: t("swiper.items.0.checkList.0.description")
                },
                {
                  title: t("swiper.items.0.checkList.1.title"),
                  description: t("swiper.items.0.checkList.1.description")
                },
                {
                  title: t("swiper.items.0.checkList.2.title"),
                  description: t("swiper.items.0.checkList.2.description")
                }
              ]
            }
          ]}
        />
        <ProductBanner
          titleh3={t("swiper.items.2.title")}
          titleh3Type="normal"
          description={t("swiper.items.2.description")}
          comp2={<NextImg src={Swiper3} />}
          checkList={[
            {
              list: [
                {
                  title: t("swiper.items.2.checkList.0.title"),
                  description: t("swiper.items.2.checkList.0.description")
                },
                {
                  title: t("swiper.items.2.checkList.1.title"),
                  description: t("swiper.items.2.checkList.1.description")
                },
                {
                  title: t("swiper.items.2.checkList.2.title"),
                  description: t("swiper.items.2.checkList.2.description")
                },
                {
                  title: t("swiper.items.2.checkList.3.title"),
                  description: t("swiper.items.2.checkList.3.description")
                }
              ]
            }
          ]}
        />
        <SuccessStory
          title={t("Success.title")}
          slogan={t("Success.slogan")}
          items={[
            {
              titleh3: t("Success.story.0.title"),
              titleh3Type: "normal",
              description: t("Success.story.0.description"),
              checkList: [
                {
                  listColor: "disabled",
                  list: [
                    { description: t("Success.story.0.checkList.0") },
                    { description: t("Success.story.0.checkList.1") }
                  ]
                }
              ],
              primaryButton: {
                content: t("Success.story.button"),
                href: "/use-case/stickey-wallet"
              },
              customerCard: {
                styleType: "bottom",
                company: t("Success.story.customer.name"),
                bgImage: YGGJBg,
                description: t("Success.story.customer.quote"),
                image: YGGJProfile
              }
            }
          ]}
        />
        <SectionLine />
        <PricingBoard
          id="Pricing"
          description={t("Pricing.description")}
          slogan={t("Pricing.slogan")}
          cards={[
            {
              title: t("Pricing.card.2.title"),
              description: t("Pricing.card.2.description"),
              button: {
                id: "gamefi-lite-plan-choose-button",
                text: t("Pricing.card.2.button"),
                onClick: (e: any) => {
                  e.preventDefault()
                  router.push(
                    { query: { plan: SolutionPlans.GameFiLite } },
                    undefined,
                    { shallow: true }
                  )
                }
              },
              productLinks: [
                ProductNames.User360,
                ProductNames.WalletBuilder,
                ProductNames.OAuthSDK,
                ProductNames.NFTAirdrop
              ],
              detail: [
                {
                  title: t("Pricing.card.2.item.1.title"),
                  list: {
                    listColor: "disabled",
                    list: [
                      {
                        title: t("Pricing.card.2.item.1.list.1.title"),
                        description: t("Pricing.card.2.item.1.list.1.content")
                      }
                    ]
                  }
                },
                {
                  title: t("Pricing.card.2.item.2.title"),
                  list: {
                    listColor: "disabled",
                    list: [
                      {
                        title: t("Pricing.card.2.item.2.list.1.title"),
                        description: t("Pricing.card.2.item.2.list.1.content")
                      }
                    ]
                  }
                },
                {
                  title: t("Pricing.card.2.item.3.title"),
                  list: {
                    listColor: "disabled",
                    list: [
                      {
                        title: t("Pricing.card.2.item.3.list.1.title"),
                        description: t("Pricing.card.2.item.3.list.1.content")
                      }
                    ]
                  }
                },
                {
                  title: t("Pricing.card.2.item.4.title"),
                  list: {
                    listColor: "disabled",
                    list: [
                      {
                        title: t("Pricing.card.2.item.4.list.1.title"),
                        description: t("Pricing.card.2.item.4.list.1.content")
                      },
                      {
                        title: t("Pricing.card.2.item.4.list.2.title"),
                        description: t("Pricing.card.2.item.4.list.2.content")
                      }
                    ]
                  }
                },
                {
                  title: t("Pricing.card.2.item.5.title"),
                  list: {
                    listColor: "disabled",
                    list: [
                      {
                        noIcon: true,
                        description: t("Pricing.card.2.item.5.list.1.content")
                      }
                    ]
                  }
                }
              ]
            },
            {
              isRecommended: true,
              title: t("Pricing.card.1.title"),
              description: t("Pricing.card.1.description"),
              button: {
                id: "gamefi-pro-plan-choose-button",
                text: t("Pricing.card.1.button"),
                onClick: (e: any) => {
                  e.preventDefault()
                  router.push(
                    { query: { plan: SolutionPlans.GameFiPro } },
                    undefined,
                    { shallow: true }
                  )
                }
              },
              productLinks: [
                ProductNames.User360,
                ProductNames.WalletBuilder,
                ProductNames.OAuthSDK,
                ProductNames.WalletAPI,
                ProductNames.WalletSDK,
                ProductNames.WalletExtension,
                ProductNames.NFTAirdrop,
                ProductNames.NFTBoost,
                ProductNames.ComplianceLite,
                ProductNames.ComplianceAPI
              ],
              detail: [
                {
                  title: t("Pricing.card.1.item.1.title"),
                  list: {
                    listColor: "disabled",
                    list: [
                      {
                        title: t("Pricing.card.1.item.1.list.1.title"),
                        description: t("Pricing.card.1.item.1.list.1.content")
                      },
                      {
                        title: t("Pricing.card.1.item.1.list.2.title"),
                        description: t("Pricing.card.1.item.1.list.2.content")
                      }
                    ]
                  }
                },
                {
                  title: t("Pricing.card.1.item.2.title"),
                  list: {
                    listColor: "disabled",
                    list: [
                      {
                        title: t("Pricing.card.1.item.2.list.1.title"),
                        description: t("Pricing.card.1.item.2.list.1.content")
                      },
                      {
                        title: t("Pricing.card.1.item.2.list.2.title"),
                        description: t("Pricing.card.1.item.2.list.2.content")
                      }
                    ]
                  }
                },
                {
                  title: t("Pricing.card.1.item.3.title"),
                  list: {
                    listColor: "disabled",
                    list: [
                      {
                        title: t("Pricing.card.1.item.3.list.1.title"),
                        description: t("Pricing.card.1.item.3.list.1.content")
                      },
                      {
                        title: t("Pricing.card.1.item.3.list.2.title"),
                        description: t("Pricing.card.1.item.3.list.2.content")
                      }
                    ]
                  }
                },
                {
                  title: t("Pricing.card.1.item.4.title"),
                  list: {
                    listColor: "disabled",
                    list: [
                      {
                        title: t("Pricing.card.1.item.4.list.1.title"),
                        description: t("Pricing.card.1.item.4.list.1.content")
                      },
                      {
                        title: t("Pricing.card.1.item.4.list.2.title"),
                        description: t("Pricing.card.1.item.4.list.2.content")
                      },
                      {
                        title: t("Pricing.card.1.item.4.list.3.title"),
                        description: t("Pricing.card.1.item.4.list.3.content")
                      },
                      {
                        title: t("Pricing.card.1.item.4.list.4.title"),
                        description: t("Pricing.card.1.item.4.list.4.content")
                      },
                      {
                        title: t("Pricing.card.1.item.4.list.5.title"),
                        description: t("Pricing.card.1.item.4.list.5.content")
                      }
                    ]
                  }
                },
                {
                  title: t("Pricing.card.1.item.5.title"),
                  list: {
                    listColor: "disabled",
                    list: [
                      {
                        title: t("Pricing.card.1.item.5.list.1.title"),
                        description: t("Pricing.card.1.item.5.list.1.content")
                      },
                      {
                        title: t("Pricing.card.1.item.5.list.2.title"),
                        description: t("Pricing.card.1.item.5.list.2.content")
                      }
                    ]
                  }
                }
              ]
            }
          ]}
        />
        <ContactUsSection
          page={SolutionNames.GameFi}
          description={t("ContactUs.description")}
          slogan={t("ContactUs.slogan")}
          body={t("ContactUs.body")}
          plans={[SolutionPlans.GameFiPro, SolutionPlans.GameFiLite]}
        />
        <Footer />
      </LayoutWrapper>
    </>
  )
}

export const getStaticProps = async ({ locale }: { locale: string }) => ({
  props: {
    ...(await serverSideTranslations(locale, ["common", "contactUs", "gamefi"]))
  }
})

export default GameFiPage
